/**
 * Arquivo centralizador para exportações do módulo de recomendação
 */

// Recomendadores
export * from './baseRecommender';
export * from './contentBasedRecommender';
export * from './collaborativeRecommender'; 
export * from './popularRecommender';
export * from './hybridRecommender';
export * from './adaptiveRecommender';

// Serviços
export * from './services/recommendationDataService';

// Utils
export * from './utils/databaseUtils';
export * from './utils/gameProcessingUtils';

// Configurações para o sistema de recomendação
export const RECOMMENDATION_CONFIG = {
  /**
   * Configurações relacionadas ao cache utilizado em diferentes partes do sistema.
   */
  cache: {
    /** Tempo de expiração padrão para itens em cache (em milissegundos). Usado principalmente pelo RecommendationDataService e CacheService. */
    defaultExpirationMs: 15 * 60 * 1000, // 15 minutos
    /** Número máximo de requisições permitidas dentro da janela de tempo definida abaixo antes de usar cache expirado (se disponível). */
    requestLimit: 50,                    
    /** Janela de tempo (em milissegundos) para a contagem do limite de requisições. */
    requestWindowMs: 60 * 1000,          // 1 minuto
  },

  /**
   * Pesos utilizados pelo RecommendationScorer para calcular a pontuação final de uma recomendação.
   * A combinação ponderada desses fatores determina a relevância percebida.
   * Idealmente, a soma dos pesos deve ser 1.0, mas não é estritamente obrigatório pela implementação atual.
   */
  scorerWeights: {
    /** Peso da similaridade (ex: cosseno) entre o embedding do usuário e o do jogo. Reflete a compatibilidade direta de gosto. */
    similarity: 0.55,
    /** Peso da popularidade geral do jogo (baseado em avaliações médias e contagem de avaliações/interações). */
    popularity: 0.15,
    /** Peso da "novidade" do jogo (baseado no ano de lançamento). Jogos mais recentes recebem um bônus. */
    recency: 0.10,
    /** Peso da diversidade. Mede o quão diferente o jogo recomendado é da coleção existente do usuário (baseado em categorias/mecânicas). */
    diversity: 0.20,
    // NOVA SUBSEÇÃO PARA PESOS DOS COMPONENTES DE POPULARIDADE
    popularityComponentWeights: {
      rating: 0.7, // Peso para a avaliação média normalizada
      count: 0.3   // Peso para a contagem de avaliações normalizada
    },
    // NOVA SUBSEÇÃO PARA AS FAIXAS DE RECÊNCIA
    recencyTiers: [
      { maxAge: 1, score: 1.0 },  // Até 1 ano
      { maxAge: 3, score: 0.8 },  // De 1 a 3 anos
      { maxAge: 5, score: 0.6 },  // De 3 a 5 anos
      { maxAge: 10, score: 0.4 }, // De 5 a 10 anos
      { maxAge: Infinity, score: 0.2 } // Mais de 10 anos (ou default)
    ],
    // NOVA PROPRIEDADE PARA PONTUAÇÃO DE ATRIBUTO DE DIVERSIDADE
    diversityAttributeScore: 0.25
  },

  /**
   * Parâmetros e thresholds utilizados pelo AlgorithmSelector para determinar qual algoritmo de recomendação usar para um usuário específico.
   */
  selectorParams: {
    // --- Condições Gerais ---
    /** Número mínimo de jogos na coleção do usuário para considerar algoritmos personalizados (ContentBased, Collaborative). Abaixo disso, geralmente usa-se Popular. */
    minCollectionSizeForPersonalized: 3,
    /** Número mínimo de avaliações (ratings) que o usuário precisa ter para considerar algoritmos personalizados. */
    minRatingCountForPersonalized: 3,
    /** Número mínimo de avaliações que o usuário precisa ter para que o algoritmo Colaborativo seja uma opção viável. */
    minRatingCountForCollaborative: 5,
    /** Número mínimo de avaliações que define um usuário como "experiente", podendo priorizar Collaborative Filtering se outras condições forem atendidas. */
    minRatingCountForExperienced: 7,
    
    // --- Similaridade para Colaborativo ---
    /** Número mínimo de usuários similares (com score acima do threshold) necessários para considerar o algoritmo Colaborativo viável. */
    minSimilarUsers: 3,
    /** Score mínimo de similaridade (da tabela user_similarities) para que outro usuário seja considerado "similar". */
    minSimilarityScore: 0.3,
    
    // --- Histórico de Sucesso ---
    /** Taxa mínima de "likes" (feedback positivo) que um algoritmo precisa ter tido para um usuário para ser considerado bem-sucedido e escolhido com base no histórico. */
    minSuccessRateThreshold: 0.3,
    /** Número mínimo de recomendações com feedback (liked/disliked) necessárias para calcular a taxa de sucesso de um algoritmo de forma confiável. */
    minFeedbackSamplesForSuccessRate: 3,
    
    // --- Pesos para Seleção Aleatória (Usuário Iniciante sem Histórico) ---
    /** Probabilidades relativas para escolher aleatoriamente entre algoritmos para usuários iniciantes que já possuem algumas avaliações, mas sem histórico de feedback em recomendações. */
    randomSelectionWeights: {
      contentBased: 0.2, // 50% de chance
      collaborative: 0.1, // 30% de chance
      popular: 0.7,       // 20% de chance
    },
  },
  
  /**
   * Thresholds gerais utilizados por diferentes algoritmos de recomendação.
   */
  recommenderThresholds: {
      /** Avaliação média mínima para um jogo ser considerado na lista inicial de "populares" pelo PopularRecommender e como fallback pelo CollaborativeRecommender. */
      minPopularityRating: 3.0, // Corrigido para escala 0-5 (era 6.0 ou 7.0)
      /** Avaliação mínima (rating) que um usuário similar precisa ter dado a um jogo para que ele seja considerado pelo CollaborativeRecommender. */
      minRatingForLiked: 4.0,
      /** Avaliação mínima (rating) que um jogo precisa ter na coleção do usuário para ser considerado um "favorito" para certas lógicas de preferência. */
      minRatingForUserFavorite: 3.5,
      /** (NOVO) Avaliação média mínima para o fallback "simples" do ContentBasedRecommender (assumindo escala 0-10 para este, ajustar se a escala for 0-5) */
      minRatingForSimpleContentFallback: 4.0,
      /** (NOVO) Número máximo de usuários similares a considerar nas queries do CollaborativeRecommender. */
      maxSimilarUsersToConsider: 10,
      // Outros exemplos comentados (se necessários no futuro):
      // minRatingImplicitLike: 3.0, // Exemplo: Usado em UserGameService para RF-023
      // maxSimilarUsersToFetch: 20, // Exemplo: Usado em CollaborativeRecommender para RF-006
  },

  /**
   * Configuração para excluir globalmente jogos que correspondam a certos critérios, independentemente do usuário ou algoritmo.
   * Útil para remover tipos de jogos indesejados (ex: expansões, demos).
   */
  globalExclusions: {
    /** Lista de categorias de jogos a serem sempre excluídas das recomendações. Case-sensitive (deve corresponder ao banco). */
    categories: ["expansão ou suplemento"],
    /** Lista de mecânicas de jogos a serem sempre excluídas. */
    mechanics: [],
    /** Lista de temas de jogos a serem sempre excluídos. */
    themes: [],
  },

  /** (NOVO) Pesos para a estratégia do HybridRecommender.ts */
  hybridStrategyWeights: {
    contentBased: 0.5,
    collaborative: 0.3,
    popular: 0.2
  },

  /**
   * Configurações específicas para o algoritmo de recomendação de jogos populares
   */
  popular: {
    /**
     * Pesos utilizados para calcular a pontuação composta dos jogos populares.
     * A pontuação determina quais jogos serão recomendados primeiro.
     */
    weights: {
      averageRating: 0.3, // Peso da avaliação média do jogo na pontuação final (0-1)
      ratingCount: 0.3,   // Peso da quantidade de avaliações do jogo na pontuação final (0-1)
      ownershipCount: 0.4 // Peso da quantidade de proprietários do jogo na pontuação final (0-1)
    },
    
    /**
     * Limitações e parâmetros gerais para a consulta de jogos populares
     */
    query: {
      /** Número máximo de jogos a buscar inicialmente antes da filtragem (deve ser maior que o limite final) */
      initialLimit: 50,
      
      /** Avaliação média mínima para considerar um jogo como "popular" */
      minAverageRating: 3.0, // Corrigido para escala 0-5
      
      /** Fator para normalizar as contagens de propriedade na pontuação composta */
      ownershipNormalizationFactor: 0.1,
    }
  },
};

/**
 * Exportação das configurações populares para manter compatibilidade
 * Será removida no futuro quando todas as referências forem atualizadas
 * @deprecated Use RECOMMENDATION_CONFIG.popular em vez disso
 */
export const POPULAR_RECOMMENDATION_CONFIG = RECOMMENDATION_CONFIG.popular; 